import React, { useEffect, useMemo, useState } from 'react'
import { Input, Loader, Message, Panel, Table } from 'rsuite'
import TrashIcon from '@rsuite/icons/Trash'
import { useTranslation } from 'react-i18next'
import { Capabilities, hasPermission } from '@/shared'
import { Button } from '@client/components/Controls'
import ModalDialog from '@client/components/ModalDialog'
import MappingHintsModel, { TMappingHint } from '@client/models/mappingHints'
import { formatDate } from '@client/utils'
import toastMessage from '@client/utils/toastMessage'

//
// What the mapping has learned, and the only way back out of it.
//
// A pair that was assigned once starts turning up as a suggestion elsewhere; if
// it was wrong, overwriting it often enough is the alternative to this list.
//
// Shows the tenant's own pairs only. Suggestions draw on what every tenant
// learned, but a name from somewhere else can only ever appear when the file at
// hand has that column — here there is no such file, so listing foreign entries
// would put foreign vocabulary on screen for the first time. Deleting stops at
// the same boundary: what one tenant learned is not another's to remove.
//
// Whoever disagrees with a suggestion that is not theirs assigns the field once
// themselves; their own pair outranks it from then on.
//

const { Column, HeaderCell, Cell } = Table

const hintsModel = new MappingHintsModel()

export default function MappingHints() {
    const { t } = useTranslation()
    const [hints, setHints] = useState<TMappingHint[] | null>(null)
    const [filter, setFilter] = useState('')
    const [error, setError] = useState('')
    const [pendingDelete, setPendingDelete] = useState<TMappingHint | null>(null)

    const readOnly = !hasPermission(Capabilities.convert_write)

    const load = async () => {
        try {
            const res = await hintsModel.list()
            setHints(res?.data ?? [])
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || t('Could not load the learned assignments.'))
            setHints([])
        }
    }

    useEffect(() => {
        load()
    }, [])

    // Both sides are searched: one arrives here knowing either the target field
    // that gets the wrong suggestion or the source field that keeps showing up.
    const rows = useMemo(() => {
        const needle = filter.trim().toLowerCase()
        if (!needle) return hints ?? []

        return (hints ?? []).filter(
            (hint) =>
                hint.target.toLowerCase().includes(needle) || hint.source.toLowerCase().includes(needle)
        )
    }, [hints, filter])

    const remove = async (hint: TMappingHint) => {
        setError('')
        try {
            await hintsModel.remove(hint._id)
            setHints((current) => (current ?? []).filter((item) => item._id !== hint._id))
            toastMessage(t('Assignment forgotten.'))
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || t('Could not delete the assignment.'))
        }
    }

    if (!hints) return <Loader content={t('Loading...')} />

    return (
        <Panel bordered header={t('Learned field assignments')}>
            <p style={{ marginBottom: '1rem' }}>
                {t('These pairs are what the mapping suggests. Delete one and it stops being suggested.')}
            </p>

            {error && (
                <Message showIcon type="error" style={{ marginBottom: '1rem' }}>
                    {error}
                </Message>
            )}

            {hints.length === 0 ? (
                <Message showIcon type="info">
                    {t('Nothing learned yet. Assigning a source field to a target field is what fills this list.')}
                </Message>
            ) : (
                <>
                    <Input
                        value={filter}
                        onChange={(value) => setFilter(String(value))}
                        placeholder={t('Search target or source field')}
                        style={{ marginBottom: '0.75rem', maxWidth: '20rem' }}
                    />

                    <Table data={rows} autoHeight rowHeight={40} affixHeader>
                        <Column flexGrow={2} minWidth={140}>
                            <HeaderCell>{t('Target field')}</HeaderCell>
                            <Cell dataKey="target" />
                        </Column>

                        <Column flexGrow={2} minWidth={140}>
                            <HeaderCell>{t('Source field')}</HeaderCell>
                            <Cell dataKey="source" />
                        </Column>

                        <Column width={110} align="right">
                            <HeaderCell>{t('Assigned')}</HeaderCell>
                            <Cell dataKey="hits" />
                        </Column>

                        {/* Kept visible: a pair with more rejects than hits is one
                            somebody kept correcting, and that is the entry worth
                            deleting. */}
                        <Column width={110} align="right">
                            <HeaderCell>{t('Rejected')}</HeaderCell>
                            <Cell dataKey="rejects" />
                        </Column>

                        <Column width={130}>
                            <HeaderCell>{t('Last used')}</HeaderCell>
                            <Cell>{(row: any) => (row.lastUsedAt ? formatDate(row.lastUsedAt) : '')}</Cell>
                        </Column>

                        <Column width={80} align="center">
                            <HeaderCell>{t('Delete')}</HeaderCell>
                            <Cell style={{ padding: '4px' }}>
                                {(row: any) => (
                                    <Button
                                        appearance="subtle"
                                        disabled={readOnly}
                                        title={t('Forget this assignment')}
                                        onClick={() => setPendingDelete(row as TMappingHint)}
                                    >
                                        <TrashIcon />
                                    </Button>
                                )}
                            </Cell>
                        </Column>
                    </Table>
                </>
            )}

            {pendingDelete && (
                <ModalDialog
                    title={t('Forget this assignment?')}
                    buttons={[
                        { label: t('Delete'), status: 'delete' },
                        { label: t('Cancel'), status: 'cancel' },
                    ]}
                    onClick={(status: string) => {
                        if (status === 'delete') remove(pendingDelete)
                        setPendingDelete(null)
                        return true
                    }}
                >
                    <p>
                        {t('{{target}} is filled from {{source}} — this will no longer be suggested.', {
                            target: pendingDelete.target,
                            source: pendingDelete.source,
                        })}
                        <br />
                        {t('Assignments already saved in a converter stay as they are.')}
                    </p>
                </ModalDialog>
            )}
        </Panel>
    )
}
